import {
  useMarketAccountPermissionFilters,
  type MarketAccountPermissionFiltersContextProps,
} from '@src/providers/MarketAccountPermissionFiltersProvider';
import {
  API_PERMISSION_WILDCARD,
  ApiPermissionActionEnum,
  intersectTwoSets,
  useMarketAccountsContext,
  type Allocation,
  type MarketAccount,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { UNSELECTED_SUB_ACCOUNT } from './consts';
import { useFeatureFlag } from './useFeatureFlag';

/** See getPermittedMarketAccountsSet for docs */
export function usePermittedMarketAccountsSet({
  subAccountAllocations,
  combineAllMarketAccounts = false,
}: Pick<GetPermittedMarketAccountsSetParams, 'subAccountAllocations' | 'combineAllMarketAccounts'>): Set<
  MarketAccount['Name']
> | null {
  const { enableAccountSegregation } = useFeatureFlag();
  const { marketAccountsList } = useMarketAccountsContext();

  const { marketAccountFilterIDsBySubAccountName, marketAccountPermissionFiltersByFilterID } =
    useMarketAccountPermissionFilters();

  // If Set<string>, only those marketAccounts included are allowed, if null, everything goes
  return useMemo(() => {
    return getPermittedMarketAccountsSet({
      subAccountAllocations,
      combineAllMarketAccounts,
      enableAccountSegregation,
      marketAccountsList,
      marketAccountFilterIDsBySubAccountName,
      marketAccountPermissionFiltersByFilterID,
    });
  }, [
    enableAccountSegregation,
    marketAccountFilterIDsBySubAccountName,
    marketAccountPermissionFiltersByFilterID,
    marketAccountsList,
    subAccountAllocations,
    combineAllMarketAccounts,
  ]);
}

export interface GetPermittedMarketAccountsSetParams {
  /**
   * The sub account allocation used to determine which market accounts are permitted.
   * If set to -1, there will be no filtering.
   */
  subAccountAllocations: Allocation[] | -1;
  /**
   * Whether return all market accounts names across all subAccounts or just intersection (default)
   */
  combineAllMarketAccounts?: boolean;
  enableAccountSegregation: boolean;
  marketAccountsList: MarketAccount[];
  marketAccountFilterIDsBySubAccountName: MarketAccountPermissionFiltersContextProps['marketAccountFilterIDsBySubAccountName'];
  marketAccountPermissionFiltersByFilterID: MarketAccountPermissionFiltersContextProps['marketAccountPermissionFiltersByFilterID'];
}

/**
 * Given the selected subaccountAllocation(s) returns
 *
 * `Set<MarketAccount['Name']>`: signifying that the included market accounts are permitted.
 *
 * or
 *
 * `null`: signifying that there are no restrictions to what market accounts that are permitted
 */
export function getPermittedMarketAccountsSet({
  subAccountAllocations,
  combineAllMarketAccounts,
  enableAccountSegregation,
  marketAccountsList,
  marketAccountFilterIDsBySubAccountName,
  marketAccountPermissionFiltersByFilterID,
}: GetPermittedMarketAccountsSetParams): Set<MarketAccount['Name']> | null {
  if (!enableAccountSegregation) {
    return null;
  }

  if (subAccountAllocations === -1) {
    return null;
  }

  const hasSubAccountAllocations = !!subAccountAllocations?.length;
  const hasUnselectedInAllocations = !!subAccountAllocations?.find(
    alloc => alloc.subAccount === UNSELECTED_SUB_ACCOUNT
  );
  const shouldReturnEmptyOptions =
    enableAccountSegregation && (!hasSubAccountAllocations || hasUnselectedInAllocations);

  // if marketaccount segregation is enabled but no selection is made, return empty set signalling nothing is selectable
  if (
    shouldReturnEmptyOptions ||
    !marketAccountFilterIDsBySubAccountName ||
    !marketAccountPermissionFiltersByFilterID
  ) {
    return new Set<string>();
  }

  const allMarketAccountNames = marketAccountsList.map(ma => ma.Name);

  let result = combineAllMarketAccounts ? new Set<string>() : new Set<string>(allMarketAccountNames);
  for (const allocation of subAccountAllocations) {
    // For each sub account provided, go through all the permission mappings and resolve all the market accounts
    // we are pointing at (or wildcard)
    const permissionFilterIDs = marketAccountFilterIDsBySubAccountName.get(allocation.subAccount) ?? [];
    const marketAccountNames = new Set<string>();
    let wildcardFound = false;
    for (const filterID of permissionFilterIDs) {
      const permissionFilter = marketAccountPermissionFiltersByFilterID.get(filterID);
      if (permissionFilter) {
        const hasRequestedAccess = permissionFilter.Action === ApiPermissionActionEnum.Write;
        const isWildCard = permissionFilter.Filter.MarketAccount === API_PERMISSION_WILDCARD;

        // Wild card rules will not allow us to narrow down the set any further
        if (isWildCard && hasRequestedAccess) {
          wildcardFound = true;
          break;
        }

        // Otherwise, just add it to the set when it grants the requested access
        if (hasRequestedAccess) {
          marketAccountNames.add(permissionFilter.Filter.MarketAccount);
        }
      }
    }

    // Two main methodologies in this hook. If combineAll = true, then _the result set starts empty_, and we _add into it_.
    // If combineAll = false, then _the result set starts with all market accounts_, and we _remove_.
    if (combineAllMarketAccounts) {
      // combine all = true. We add market accounts as we find them.
      if (wildcardFound) {
        result = new Set([...result, ...allMarketAccountNames]); // wildcard -> add all
      } else {
        result = new Set([...result, ...marketAccountNames]); // not wildcard -> add the specific mapping
      }
    } else {
      // combine all = false, so we are _removing_ accounts. If wildcard = true, _do nothing_
      if (!wildcardFound) {
        result = intersectTwoSets(result, marketAccountNames); // intersect existing accounts by the found mapping (removal operation)
      }
    }
  }

  return result;
}
